import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Autocomplete, TextField, Table, TableHead, TableBody, TableRow, TableCell, Typography,
} from "@mui/material";

import { ISalary } from "../../interface/salary";
import { IRegieSimulation } from "../../interface/regie";
import { getRegieSimulationsBySalary } from "../../tools/regie/getRegieSimulationsBySalary";

interface RegieHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  salaries: ISalary[];
  defaultSalary: ISalary | null;
}

export default function RegieHistoryDialog({ open, onClose, salaries, defaultSalary }: RegieHistoryDialogProps) {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);

  const [selectedSalary, setSelectedSalary] = useState<ISalary | null>(defaultSalary);
  const [simulations, setSimulations] = useState<IRegieSimulation[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) setSelectedSalary(defaultSalary);
  }, [open, defaultSalary]);

  useEffect(() => {
    if (!open || !token || !selectedSalary) { setSimulations([]); return; }
    getRegieSimulationsBySalary(selectedSalary._id, token, setSimulations, dispatch, setError);
  }, [open, token, selectedSalary, dispatch]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Historique des simulations régie</DialogTitle>
      <DialogContent>
        <Autocomplete
          sx={{ mb: 3, mt: 1, maxWidth: 400 }}
          options={salaries}
          getOptionLabel={(option: ISalary) => `${option.firstname} ${option.lastname}`}
          isOptionEqualToValue={(option, value) => option._id === value?._id}
          value={selectedSalary}
          onChange={(_e, value) => setSelectedSalary(value)}
          renderInput={(params) => <TextField {...params} label="Salarié" />}
        />

        {error && <Typography color="error">{error}</Typography>}

        {selectedSalary && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>TJM proposé</TableCell>
                <TableCell>TJM à appliquer</TableCell>
                <TableCell>Marge relative annuelle</TableCell>
                <TableCell>Créée par</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {simulations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">Aucune simulation enregistrée pour ce salarié.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {simulations.map((simulation) => (
                <TableRow key={simulation._id}>
                  <TableCell>{new Date(simulation.createdAt).toLocaleString("fr-FR")}</TableCell>
                  <TableCell>{simulation.inputs.tjmPropose.toLocaleString("fr-FR")} €</TableCell>
                  <TableCell>{simulation.results.tjmAAppliquer.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €</TableCell>
                  <TableCell>{(simulation.results.margeRelativeAnnuelle * 100).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %</TableCell>
                  <TableCell>{simulation.createdBy?.email || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}
